import React from 'react';
import { account } from '../copData';

// Renders a representative preview of the selected creative template. Onsite
// templates render as the on-page modal/widget; email templates render as an
// email layout. Content adapts to the chosen category and the segment's offer
// so the preview changes as the operator switches templates.

const PRODUCTS = [
	{ name: 'Featured Collectible', price: '$129' },
	{ name: 'Limited Edition', price: '$249' },
	{ name: 'Fan Favorite', price: '$89' },
];

function Countdown({ countdown }) {
	const { h, m, s } = countdown;
	return (
		<div className="cop-countdown">
			{[[h, 'hours'], [m, 'minutes'], [s, 'seconds']].map(([v, l]) => (
				<div className="cop-count-cell" key={l}>
					<div className="v">{v}</div>
					<div className="l">{l}</div>
				</div>
			))}
		</div>
	);
}

// ---------------------------------------------------------------- onsite ---

function OnsiteOffer({ selected, countdown }) {
	const headline = selected.discount_pct > 0 ? 'YOUR EXCLUSIVE OFFER EXPIRES SOON' : 'RESERVED FOR YOU — EARLY ACCESS';
	return (
		<>
			<h2>{headline}</h2>
			<p className="sub">Our AI detected high purchase intent. Claim your personalized deal before it expires.</p>
			<Countdown countdown={countdown} />
			<div className="cop-coupon">{selected.offer_token}</div>
			<button className="cop-claim-btn" onClick={(e) => e.preventDefault()}>CLAIM YOUR OFFER</button>
		</>
	);
}

function OnsiteSurvey() {
	return (
		<>
			<div className="cop-pv-eyebrow">Quick question</div>
			<h2 style={{ fontSize: 18 }}>What brought you in today?</h2>
			<div className="cop-pv-optrow">
				{['Just browsing', 'Looking for a gift', 'Ready to buy'].map((o) => (
					<button key={o} className="cop-pv-opt" onClick={(e) => e.preventDefault()}>{o}</button>
				))}
			</div>
			<div className="cop-pv-dots">
				{[0, 1, 2].map((i) => (
					<span key={i} className={`cop-pv-dot${i === 0 ? ' on' : ''}`} />
				))}
			</div>
		</>
	);
}

function OnsiteLead({ selected }) {
	return (
		<>
			<div className="cop-pv-eyebrow">{selected.discount_pct ? `${selected.discount_pct}% off your first order` : 'Members get early access'}</div>
			<h2 style={{ fontSize: 19 }}>Unlock your reward</h2>
			<p className="sub">Drop your email and we’ll send your code instantly.</p>
			<div className="cop-pv-field">you@email.com</div>
			<button className="cop-claim-btn" onClick={(e) => e.preventDefault()}>
				{selected.discount_pct ? 'Send my code' : 'Get early access'}
			</button>
			<button className="cop-pv-link" onClick={(e) => e.preventDefault()}>No thanks, maybe later</button>
		</>
	);
}

function OnsiteRecs() {
	return (
		<>
			<h2 style={{ fontSize: 18 }}>Picked for you</h2>
			<p className="sub">Based on what you’ve been viewing.</p>
			<div className="cop-pv-products">
				{PRODUCTS.map((p) => (
					<div className="cop-pv-product" key={p.name}>
						<div className="cop-pv-thumb" />
						<div className="cop-pv-name">{p.name}</div>
						<div className="cop-pv-price">{p.price}</div>
					</div>
				))}
			</div>
			<button className="cop-claim-btn" onClick={(e) => e.preventDefault()}>Add to cart</button>
		</>
	);
}

function OnsiteCart({ selected }) {
	return (
		<>
			<h2 style={{ fontSize: 18 }}>Still thinking it over?</h2>
			<p className="sub">Your cart is saved — finish before it’s gone.</p>
			<div className="cop-pv-cartlist">
				{PRODUCTS.slice(0, 2).map((p) => (
					<div className="cop-pv-cartrow" key={p.name}>
						<span className="cop-pv-thumb sm" />
						<span className="cop-pv-name">{p.name}</span>
						<span className="cop-pv-price">{p.price}</span>
					</div>
				))}
			</div>
			<div className="cop-pv-totals">
				<div className="cop-pv-total-row"><span>Subtotal</span><span>$378</span></div>
				{selected.discount_pct > 0 && (
					<div className="cop-pv-total-row"><span>Discount ({selected.offer_token})</span><span>-{selected.discount_pct}%</span></div>
				)}
				<div className="cop-pv-total-row strong"><span>Total</span><span>{selected.discount_pct ? '$321' : '$378'}</span></div>
			</div>
			<button className="cop-claim-btn" onClick={(e) => e.preventDefault()}>Complete checkout</button>
		</>
	);
}

const ONSITE_BY_CATEGORY = {
	survey: OnsiteSurvey,
	lead_capture: OnsiteLead,
	recs: OnsiteRecs,
	active_cart: OnsiteCart,
};

// ----------------------------------------------------------------- email ---

function EmailFrame({ children }) {
	const merchant = account?.merchant_name || 'Your Store';
	return (
		<div className="cop-email-frame">
			<div className="cop-email-header">
				<span className="cop-email-logo">{merchant}</span>
				<span className="cop-email-unsub">Unsubscribe</span>
			</div>
			{children}
			<div className="cop-email-footer">
				<div className="cop-email-social">
					{[0, 1, 2, 3].map((i) => (
						<span key={i} className="cop-email-soc" />
					))}
				</div>
				<div className="cop-email-foot-links">Shop · Account · Help</div>
				<div className="cop-email-legal">© {merchant} · You’re receiving this because you shopped with us.</div>
			</div>
		</div>
	);
}

function EmailHero({ selected, template }) {
	const align = /right/i.test(template?.name) ? 'right' : /center/i.test(template?.name) ? 'center' : 'overlay';
	return (
		<EmailFrame>
			{align === 'overlay' ? (
				<div className="cop-email-hero">
					<div className="cop-email-hero-eyebrow">{selected.discount_pct ? `${selected.discount_pct}% off, just for you` : 'Just for you'}</div>
					<div className="cop-email-hero-h">We saved your spot</div>
					<button className="cop-email-cta" onClick={(e) => e.preventDefault()}>Shop now</button>
				</div>
			) : (
				<div className={`cop-email-split ${align}`}>
					{align === 'right' && (
						<div className="cop-email-col">
							<div className="cop-email-h">We saved your spot</div>
							<div className="cop-email-body-text">A little something to bring you back.</div>
							<button className="cop-email-cta sm" onClick={(e) => e.preventDefault()}>Shop now</button>
						</div>
					)}
					<div className="cop-pv-thumb email-product" />
					{align === 'center' && (
						<div className="cop-email-h" style={{ textAlign: 'center' }}>We saved your spot</div>
					)}
				</div>
			)}
		</EmailFrame>
	);
}

function EmailCart({ selected }) {
	return (
		<EmailFrame>
			<div className="cop-email-sec-h">You left something behind</div>
			<div className="cop-email-cart">
				{PRODUCTS.slice(0, 2).map((p) => (
					<div className="cop-email-cart-row" key={p.name}>
						<span className="cop-pv-thumb sm" />
						<span className="cop-pv-name">{p.name}</span>
						<span className="cop-pv-price">{p.price}</span>
					</div>
				))}
				<div className="cop-pv-totals tight">
					<div className="cop-pv-total-row"><span>Subtotal</span><span>$378</span></div>
					{selected.discount_pct > 0 && (
						<div className="cop-pv-total-row"><span>Discount</span><span>-{selected.discount_pct}%</span></div>
					)}
				</div>
			</div>
			<button className="cop-email-cta" onClick={(e) => e.preventDefault()}>Return to cart</button>
		</EmailFrame>
	);
}

function EmailRecs() {
	return (
		<EmailFrame>
			<div className="cop-email-sec-h">Recommended for you</div>
			<div className="cop-email-recs">
				{PRODUCTS.map((p) => (
					<div className="cop-email-rec" key={p.name}>
						<span className="cop-pv-thumb" />
						<span className="cop-pv-name">{p.name}</span>
						<span className="cop-pv-price">{p.price}</span>
					</div>
				))}
			</div>
			<button className="cop-email-cta" onClick={(e) => e.preventDefault()}>Shop all</button>
		</EmailFrame>
	);
}

function EmailPromo({ selected }) {
	return (
		<EmailFrame>
			<div className="cop-email-promo">
				<div className="cop-email-promo-tag">{selected.discount_pct ? `${selected.discount_pct}% OFF` : 'New In'}</div>
			</div>
			<div className="cop-email-sec-h" style={{ textAlign: 'center' }}>A reason to come back</div>
			<div className="cop-email-body-text" style={{ textAlign: 'center' }}>Use code {selected.offer_token} at checkout.</div>
			<button className="cop-email-cta" onClick={(e) => e.preventDefault()}>Redeem offer</button>
		</EmailFrame>
	);
}

const EMAIL_BY_CATEGORY = {
	email_hero: EmailHero,
	email_cart: EmailCart,
	email_recs: EmailRecs,
	email_promo: EmailPromo,
};

export default function TemplatePreview({ channel, categoryCode, template, selected, device, countdown }) {
	if (channel === 'email') {
		const EmailComp = EMAIL_BY_CATEGORY[categoryCode] || EmailHero;
		return (
			<div className="cop-email-wrap" key={categoryCode + template.node_id + device} style={device === 'mobile' ? { maxWidth: 340 } : undefined}>
				<EmailComp selected={selected} template={template} />
				<div className="cop-pv-caption">{template.name}</div>
			</div>
		);
	}

	const OnsiteComp = ONSITE_BY_CATEGORY[categoryCode];
	return (
		<div
			className="cop-preview-modal"
			key={categoryCode + template.node_id + device}
			style={device === 'mobile' ? { maxWidth: 300 } : undefined}
		>
			{OnsiteComp ? <OnsiteComp selected={selected} countdown={countdown} /> : <OnsiteOffer selected={selected} countdown={countdown} />}
			<div className="cop-pv-caption">{template.name}</div>
		</div>
	);
}
